import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TourStep {
  target: string; // CSS selector
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  disableBeacon?: boolean;
}

interface ProductTourProps {
  steps: TourStep[];
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
  onStepChange?: (stepIndex: number) => void;
}

export function ProductTour({ steps, isOpen, onClose, onComplete, onStepChange }: Readonly<ProductTourProps>) {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (!isOpen || steps.length === 0) return;

    const step = steps[currentStep];
    const element = document.querySelector(step.target) as HTMLElement;

    if (element) {
      // Check if element or any of its children are positioned (fixed/absolute)
      const computedPosition = window.getComputedStyle(element).position;
      
      // Check if this is the right panel wrapper or contains absolutely positioned children
      const isRightPanelWrapper = element.getAttribute('data-tour') === 'right-panel';
      const hasAbsoluteChild = element.querySelector('[style*="position: absolute"], [style*="position: fixed"]') !== null;
      
      // Check if any direct child has absolute positioning via computed styles
      let hasAbsoluteChildComputed = false;
      for (const child of Array.from(element.children)) {
        const childPosition = window.getComputedStyle(child).position;
        if (childPosition === 'absolute' || childPosition === 'fixed') {
          hasAbsoluteChildComputed = true;
          break;
        }
      }
      
      // Check if this is the right panel wrapper - it should be highlighted but NOT scrolled or modified
      const shouldSkipModifications = isRightPanelWrapper || hasAbsoluteChild || hasAbsoluteChildComputed;
      
      // For positioned elements, don't modify them at all - just highlight the child instead
      if (shouldSkipModifications) {
        // Find the absolutely positioned child and highlight that instead
        const absoluteChild = Array.from(element.children).find(child => {
          const childPosition = window.getComputedStyle(child as HTMLElement).position;
          return childPosition === 'absolute' || childPosition === 'fixed';
        }) as HTMLElement;
        
        if (absoluteChild) {
          const originalBoxShadow = absoluteChild.style.boxShadow;
          const originalBorderRadius = absoluteChild.style.borderRadius;
          const originalOverflow = absoluteChild.style.overflow;
          
          // Use a smaller, inset glow that won't be clipped
          absoluteChild.style.boxShadow = 'inset 0 0 0 4px rgba(59, 130, 246, 1), inset 0 0 20px rgba(59, 130, 246, 0.6), 0 0 0 2px rgba(59, 130, 246, 0.8)';
          absoluteChild.style.borderRadius = '8px';
          absoluteChild.style.overflow = 'visible';
          
          return () => {
            absoluteChild.style.boxShadow = originalBoxShadow;
            absoluteChild.style.borderRadius = originalBorderRadius;
            absoluteChild.style.overflow = originalOverflow;
          };
        }
        return;
      }
      
      // Store original styles for normal elements
      const originalPosition = element.style.position;
      const originalZIndex = element.style.zIndex;
      const originalBoxShadow = element.style.boxShadow;
      const originalBorderRadius = element.style.borderRadius;
      
      // Highlight the target element
      if (computedPosition === 'static') {
        element.style.position = 'relative';
      }
      element.style.zIndex = '9999';
      element.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.8), 0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(59, 130, 246, 0.3)';
      element.style.borderRadius = '8px';
      
      // Scroll into view
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });

      return () => {
        // Restore original styles
        element.style.position = originalPosition;
        element.style.zIndex = originalZIndex;
        element.style.boxShadow = originalBoxShadow;
        element.style.borderRadius = originalBorderRadius;
      };
    }
  }, [currentStep, isOpen, steps]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      onStepChange?.(nextStep);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      onStepChange?.(prevStep);
    }
  };

  const handleComplete = () => {
    setCurrentStep(0);
    onComplete?.();
    onClose();
  };

  const handleSkip = () => {
    setCurrentStep(0);
    onClose();
  };

  if (!isOpen || steps.length === 0) return null;

  const step = steps[currentStep];

  return (
    <>
      {/* Overlay */}
      <button 
        type="button"
        className="fixed inset-0 bg-black/50 z-[9998] cursor-default border-none p-0"
        onClick={handleSkip}
        aria-label="Close tour"
      />

      {/* Tour Card - Fixed at bottom center */}
      <Card
        className="fixed z-[10000] w-[400px] shadow-2xl bottom-8 left-1/2 -translate-x-1/2"
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <CardTitle className="text-lg">{step.title}</CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 -mt-1 -mr-1"
              onClick={handleSkip}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pb-3">
          <p className="text-sm text-muted-foreground">{step.content}</p>
        </CardContent>
        <CardFooter className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-1">
            {steps.map((stepItem, index) => (
              <div
                key={`step-indicator-${stepItem.target}`}
                className={cn(
                  'h-2 w-2 rounded-full transition-colors',
                  index === currentStep ? 'bg-primary' : 'bg-muted'
                )}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              size="sm"
              onClick={handleNext}
            >
              {currentStep === steps.length - 1 ? (
                'Finish'
              ) : (
                <>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </>
  );
}
